import React, {
  Fragment,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  ProviderEntryConfigDTO,
} from 'dtos';
import Controller from 'core/components/FormController';
import PhoneInputWrapper from 'core/components/PhoneInputWrapper';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Input from 'pages/Dashboard/components/Input';
import {
  validatePhone,
  validateEmail,
} from 'pages/Dashboard/utils/helper';

type ContactField = 'phone' | 'fax' | 'email';
type Props = {
  fields?: ContactField[];
  requiredFields?: ContactField[];
  disabled?: boolean;
  idPrefix?: string;
};

export default function ProviderContactFields({
  fields = ['phone', 'fax', 'email'],
  requiredFields = [],
  disabled = false,
  idPrefix = 'provider-contact',
}: Props) {
  const { control } = useFormContext<ProviderEntryConfigDTO>();
  const isFaxRequired = requiredFields.includes('fax');
  const isPhoneRequired = requiredFields.includes('phone');
  const isEmailRequired = requiredFields.includes('email');
  const contactRows = [
    {
      key: 'phone' as const,
      icon: <PhoneOutlinedIcon className="!w-4 !h-4 text-gray-500" />,
      label: 'Phone',
      field: (
        <Controller
          name="phoneNumber.phoneNumberWithAreaCode"
          control={control}
          rules={isPhoneRequired ? {
            required: 'Phone number is required.',
            validate: (value) => validatePhone(value) || 'Enter a valid phone number.',
          } : undefined}
          render={({ field, fieldState: { invalid } }) => (
            <PhoneInputWrapper
              {...field}
              id={`${idPrefix}-phone`}
              name={`${idPrefix}-phone`}
              placeholder="Phone"
              fieldKey="phoneNumber.phoneNumberWithAreaCode"
              disabled={disabled}
              required={isPhoneRequired}
              error={invalid}
            />
          )}
        />
      ),
    },
    {
      key: 'fax' as const,
      icon: <PrintOutlinedIcon className="!w-4 !h-4 text-gray-500" />,
      label: 'Fax',
      field: (
        <Controller
          name="faxNumber"
          control={control}
          rules={isFaxRequired ? {
            required: 'Fax number is required.',
            validate: (value) => validatePhone(value) || 'Enter a valid fax number.',
          } : undefined}
          render={({ field, fieldState: { invalid } }) => (
            <PhoneInputWrapper
              {...field}
              id={`${idPrefix}-fax`}
              name={`${idPrefix}-fax`}
              placeholder="Fax"
              fieldKey="faxNumber"
              required={isFaxRequired}
              error={invalid}
              disabled={disabled}
              aria-label={isFaxRequired ? 'Fax number' : undefined}
            />
          )}
        />
      ),
    },
    {
      key: 'email' as const,
      icon: <EmailOutlinedIcon className="!w-4 !h-4 text-gray-500" />,
      label: 'Email',
      field: (
        <Controller
          name="email"
          control={control}
          rules={isEmailRequired ? {
            required: 'Email is required.',
            validate: (value) => validateEmail(value) || 'Enter a valid email.',
          } : undefined}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              {...field}
              id={`${idPrefix}-email`}
              placeholder="Email"
              className="w-full"
              disabled={disabled}
              required={isEmailRequired}
              error={invalid}
            />
          )}
        />
      ),
    },
  ];

  return (
    <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-2 gap-y-form-row">
      {contactRows.filter(({ key }) => fields.includes(key)).map(({ key, icon, label, field }) => (
        <Fragment key={key}>
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
            {icon}
            <span>
              {label}
              {requiredFields.includes(key) && <span className="text-red-400"> *</span>}
            </span>
          </div>
          <Wrapper id={`${idPrefix}-${key}`} className="min-w-0">
            {field}
          </Wrapper>
        </Fragment>
      ))}
    </div>
  );
}
